import { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormFeedback, Button, Spinner, Row, Col } from 'reactstrap';
// See dataTable.jsx — Vite pre-bundles react-windowed-select's CJS default
// without unwrapping, so we normalize here.
import WindowedSelectModule from 'react-windowed-select';
const WindowedSelect = WindowedSelectModule.default ?? WindowedSelectModule;
import useTheme from '../../hooks/useTheme';
import { customSelectTheme, selectStyle } from '../utility/selectStyling';
import { COUNTRIES } from '../../utils/countries';
import { CURRENCIES, DEFAULT_CURRENCY } from '../../constants/currencies';
import { validateCustomer } from '../../utils/customerValidation';
import useCreateCustomer from '../../hooks/useCreateCustomer';

const COUNTRY_OPTIONS = COUNTRIES.map(c => ({ value: c.code, label: c.name }));

export default function CreateCustomerModal({ isOpen, onClose }) {
    const theme = useTheme();
    const createCustomer = useCreateCustomer();

    const [name, setName] = useState('');
    const [contactName, setContactName] = useState('');
    const [country, setCountry] = useState(null);
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [addressLine1, setAddressLine1] = useState('');
    const [addressLine2, setAddressLine2] = useState('');
    const [townCity, setTownCity] = useState('');
    const [postcode, setPostcode] = useState('');
    // Customer currency is the prefill default for jobs — jobs copy it on
    // create but then carry their own authoritative code. Default GBP keeps
    // the UK-first experience unchanged for new users.
    const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
    // Only surface validation errors once the user has actually tried to
    // submit — showing red borders on an empty form is noisy.
    const [attempted, setAttempted] = useState(false);

    const { errors, hasErrors } = validateCustomer({
        name, contactName, country, email, phone,
        addressLine1, townCity, postcode,
    });

    const showNameError          = attempted && errors.name;
    const showContactNameError   = attempted && errors.contactName;
    const showCountryError       = attempted && errors.country;
    const showEmailFormatError   = attempted && errors.email;
    const showPhoneFormatError   = attempted && errors.phone;
    const showContactMethodError = attempted && errors.contactMethod;
    const showAddressLine1Error  = attempted && errors.addressLine1;
    const showTownCityError      = attempted && errors.townCity;
    const showPostcodeError      = attempted && errors.postcode;

    const reset = () => {
        setName(''); setContactName(''); setCountry(null);
        setEmail(''); setPhone('');
        setAddressLine1(''); setAddressLine2('');
        setTownCity(''); setPostcode('');
        setCurrency(DEFAULT_CURRENCY);
        setAttempted(false);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (createCustomer.isPending) return;
        if (hasErrors) {
            setAttempted(true);
            return;
        }
        await createCustomer.mutateAsync({
            name:         name.trim(),
            contactName:  contactName.trim(),
            country,                                   // already an ISO alpha-2 code from the dropdown
            email:        email.trim() || null,
            phone:        phone.trim() || null,
            addressLine1: addressLine1.trim(),
            addressLine2: addressLine2.trim() || null,
            townCity:     townCity.trim(),
            postcode:     postcode.trim(),
            currency,
        });
        reset();
        onClose();
    };

    return (
        <Modal isOpen={isOpen} toggle={handleClose} centered backdrop="static" size="lg">
            <ModalHeader toggle={handleClose}>New Customer</ModalHeader>
            <Form onSubmit={handleSubmit} noValidate>
                <ModalBody>
                    <FormGroup>
                        <Label>Name *</Label>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            invalid={!!showNameError}
                        />
                        <FormFeedback>{errors.name}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>Contact Name *</Label>
                        <Input
                            value={contactName}
                            onChange={(e) => setContactName(e.target.value)}
                            invalid={!!showContactNameError}
                        />
                        <FormFeedback>{errors.contactName}</FormFeedback>
                    </FormGroup>
                    <Row>
                        <Col md={8}>
                            <FormGroup>
                                <Label>Country *</Label>
                                <WindowedSelect
                                    options={COUNTRY_OPTIONS}
                                    value={COUNTRY_OPTIONS.find(o => o.value === country) ?? null}
                                    onChange={(opt) => setCountry(opt?.value ?? null)}
                                    isClearable isSearchable
                                    styles={selectStyle}
                                    theme={customSelectTheme(theme)}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    placeholder="Select a country..."
                                />
                                {showCountryError && (
                                    <div className="invalid-feedback d-block">{errors.country}</div>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label>Currency</Label>
                                <Input type="select" value={currency}
                                       onChange={(e) => setCurrency(e.target.value)}>
                                    {CURRENCIES.map((c) => (
                                        <option key={c.code} value={c.code}>
                                            {c.code} — {c.symbol} {c.name}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Email *</Label>
                                <Input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    invalid={!!showEmailFormatError}
                                />
                                <FormFeedback>{errors.email}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Phone *</Label>
                                <Input
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    invalid={!!showPhoneFormatError}
                                />
                                <FormFeedback>{errors.phone}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>
                    {showContactMethodError && (
                        <div className="invalid-feedback d-block mb-3">{errors.contactMethod}</div>
                    )}
                    <FormGroup>
                        <Label>Address Line 1 *</Label>
                        <Input
                            value={addressLine1}
                            onChange={(e) => setAddressLine1(e.target.value)}
                            invalid={!!showAddressLine1Error}
                        />
                        <FormFeedback>{errors.addressLine1}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>Address Line 2</Label>
                        <Input
                            value={addressLine2}
                            onChange={(e) => setAddressLine2(e.target.value)}
                        />
                    </FormGroup>
                    <Row>
                        <Col md={8}>
                            <FormGroup>
                                <Label>Town/City *</Label>
                                <Input
                                    value={townCity}
                                    onChange={(e) => setTownCity(e.target.value)}
                                    invalid={!!showTownCityError}
                                />
                                <FormFeedback>{errors.townCity}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label>Postcode *</Label>
                                <Input
                                    value={postcode}
                                    onChange={(e) => setPostcode(e.target.value)}
                                    invalid={!!showPostcodeError}
                                />
                                <FormFeedback>{errors.postcode}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" outline onClick={handleClose}
                            disabled={createCustomer.isPending}>Cancel</Button>
                    <Button color="primary" type="submit" disabled={createCustomer.isPending}>
                        {createCustomer.isPending && <Spinner size="sm" className="me-1" />}
                        Create
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
}
